import { apiWrapper } from '../../config/api-wrapper';
import environment from '../../config/env';
import { SystemHealthEnvironmentStatus, SystemHealthStatus } from './systems-health-model';

const NGC_API_ENVS: string[] = environment.NGC_API_ENVS;

const mapSelfSystemStatusToStandard = (apiData: any): boolean => {
  return apiData.status === 'UP';
};

const mapStandardSystemStatus = (apiData: any): SystemHealthStatus => {
  return apiData ? SystemHealthStatus.OK : SystemHealthStatus.DOWN;
};

const handleSystemStatusPromise = async <T>(systemStatusPromise: Promise<T>): Promise<SystemHealthStatus> => {
  try {
    return mapStandardSystemStatus(await systemStatusPromise);
  } catch {
    return SystemHealthStatus.DOWN;
  }
};

class SystemHealthService {
  public getSelfStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(
      apiWrapper.api.get('/actuator/health').then(res => mapSelfSystemStatusToStandard(res.data)),
    );
  }

  public getAmwayProfileStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/test/profile'));
  }

  public getUplinesStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/test/uplines'));
  }

  public getDocgenStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/test/docgen'));
  }

  public getOrderApiStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/health/order-api'));
  }

  public getItemApiStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/health/item-api'));
  }

  public getVolumeApiStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('api/health/volume-api'));
  }

  public getExponeaStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.adminApi.get('health/exponea-health'));
  }

  public getPublishAPIStatus(): Promise<SystemHealthStatus> {
    return handleSystemStatusPromise(apiWrapper.api.get('admin/actuator/health/liveness'));
  }

  public getNgcOrderDetailsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/order-detail');
  }

  public getNgcReturnDetailsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/return-detail');
  }

  public getNgcAccountDetailsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/account-details');
  }

  public getNgcAccountMappingsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/account-mappings');
  }

  public getNgcAccountsByProfileIdApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/accounts-by-profile-id');
  }

  public getNgcPaymentsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/payments');
  }

  public getNgcProfileByPartyIdStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/profile-by-party-id');
  }

  public getNgcProfileApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/profile');
  }

  public getNgcUserApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('api/health/user');
  }

  public getNgcAccountEmailsApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('/api/health/account-emails');
  }

  public getNgcAccountPhonesApiStatus(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('/api/health/account-phones');
  }

  public getNgcAnonymousDeliveryTrackingApi(): Promise<SystemHealthEnvironmentStatus[]> {
    return this.getEnvironmentSystemHealthStatuses('/api/health/anonymous-delivery-tracking');
  }

  private async getEnvironmentSystemHealthStatuses(endpoint: string) {
    const environmentStatuses: SystemHealthEnvironmentStatus[] = [];
    for (const env of NGC_API_ENVS) {
      environmentStatuses.push({
        environment: env,
        status: await handleSystemStatusPromise(apiWrapper.api.get(`${endpoint}?sourceEnv=${env}`)),
      });
    }
    return environmentStatuses;
  }
}

const systemHealthService = new SystemHealthService();
export default systemHealthService;
